/**
 * Xhelos Debug Tools - Unified Version
 * Merges all debug functionality into one place
 */

import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import useUiEffectsStore from '@store/useUiEffectsStore';
import useCinematicStore from '@store/useCinematicStore';
import useMapStore from '@store/useMapStore';
import useSelectionStore from '@store/useSelectionStore';
import useMetadataStore from '@store/useMetadataStore';
import useConfigStore from '@store/useConfigStore';
import calculateActionOrigins from '@helpers/game/calculateActionOrigins';
import { validateGameSnapshots, validateUnitAcrossSnapshots, validateUnits } from './unitValidator';
import { CONFIG } from './constants';
import { Asset } from '@rts';

/**
 * Wrapper class that adds .str() method to debug results
 * Allows chaining like: x.anim(1).str() to get copyable JSON
 */
class DebugResult {
  constructor(value) {
    this._value = value;
  }

  /**
   * Returns JSON string representation for easy copy-paste
   * Just returns the string - no console.log to avoid duplicates
   */
  str() {
    try {
      return JSON.stringify(this._value, null, 2);
    } catch (e) {
      // Handle circular references or non-serializable values
      console.error('Could not stringify:', e.message);
      return String(this._value);
    }
  }

  /**
   * Get the raw value
   */
  valueOf() {
    return this._value;
  }
}

/**
 * Wraps a value in DebugResult for .str() support
 */
function wrapResult(value) {
  return new DebugResult(value);
}

// Create the unified debug object
const xhelosDebugger = {
  // === SNAPSHOT FUNCTIONS ===
  
  /**
   * Get RAW snapshot (not merged) - gets the actual delta or base/current
   */
  getRawSnapshot(key) {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame) {
      console.error('❌ No raw game data loaded');
      return null;
    }
    
    // In PLAN/DEPLOY phase, there might not be snapshots
    if (!rawGame.snapshots) {
      if (key === 'current' || key === 'base') {
        console.log(`📸 No snapshots in ${rawGame.status} phase - returning full game`);
        return rawGame;
      }
      console.warn('⚠️ No snapshots available in current game phase');
      return null;
    }
    
    const snapshot = rawGame.snapshots[key];
    if (!snapshot) {
      console.error(`❌ Raw snapshot '${key}' not found`);
      console.log('📌 Available snapshots:', Object.keys(rawGame.snapshots));
      return null;
    }
    
    // Check if this is a sparse delta (numbered snapshots)
    const isDelta = !isNaN(parseInt(key));
    const label = isDelta ? `Delta #${key}` : `Raw ${key}`;
    
    console.groupCollapsed(`📸 ${label} Snapshot`);
    
    // Show what's in this snapshot
    if (snapshot.dynamic_objects?.units) {
      const units = snapshot.dynamic_objects.units;
      if (isDelta && units.length < 10) {
        // For deltas, show full unit data since it's sparse
        console.log('Changed units:', units);
      } else {
        // For base/current, show table
        console.table(units.map(u => ({
          id: u.id,
          name: u.name || '❌ MISSING',
          hp: `${u.hp}/${u.max_hp || '?'}`,
          pos: `(${u.x}, ${u.y})`,
          team: u.teamClass || '?'
        })));
      }
    }
    
    console.log('Full snapshot:', snapshot);
    console.groupEnd();

    return wrapResult(snapshot);
  },
  
  /**
   * Get MERGED/CALCULATED snapshot at a specific point
   */
  getSnapshot(key) {
    const state = useGameStore.getState();
    
    // Special case: get current view
    if (key === 'view' || key === undefined) {
      const game = state.game;
      console.log(`📸 Current view (merged snapshot at index: ${state.currentSnapshotIndex})`);
      return game;
    }
    
    // Use the store's getGameByIndex function which handles merging
    const mergedGame = state.getGameByIndex(key);
    
    if (!mergedGame) {
      console.error(`❌ Could not get merged snapshot for '${key}'`);
      return null;
    }
    
    console.groupCollapsed(`📸 Merged Snapshot: ${key}`);
    
    // Show merged units
    if (mergedGame.dynamic_objects?.units) {
      console.table(mergedGame.dynamic_objects.units.map(u => ({
        id: u.id,
        name: u.name || '❌ MISSING',
        hp: `${u.hp}/${u.max_hp || '?'}`,
        pos: `(${u.x}, ${u.y})`,
        team: u.teamClass || '?',
        alive: u.is_alive ? '✅' : '💀'
      })));
    }
    
    console.log('Full merged game:', mergedGame);
    console.groupEnd();

    return wrapResult(mergedGame);
  },
  
  /**
   * Get only the delta changes at a specific step
   */
  getDeltaSnapshot(step) {
    if (typeof step !== 'number' || step < 0) {
      console.error('❌ Step must be a non-negative number');
      return null;
    }
    
    return this.getRawSnapshot(step);
  },
  
  /**
   * Show all available snapshots
   */
  listSnapshots() {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame?.snapshots) {
      console.error('❌ No snapshots available');
      return;
    }
    
    const snapshots = Object.keys(rawGame.snapshots);
    console.log('📸 Available snapshots:', snapshots);
    
    // Categorize them
    const base = snapshots.includes('base');
    const current = snapshots.includes('current');
    const deltas = snapshots.filter(k => !isNaN(parseInt(k))).sort((a, b) => parseInt(a) - parseInt(b));
    
    console.log('  Base:', base ? '✅' : '❌');
    console.log('  Current:', current ? '✅' : '❌');
    console.log('  Deltas:', deltas.length > 0 ? deltas.join(', ') : 'None');

    return wrapResult(snapshots);
  },
  
  // === UNIT FUNCTIONS ===
  
  /**
   * Get a specific unit by ID from current game state
   */
  getUnit(unitId) {
    const unit = useGameStore.getState().getUnitById(unitId);

    if (unit) {
      console.log(`✅ Found Unit #${unitId}:`, unit);
      return wrapResult(unit);
    } else {
      console.error(`❌ Unit with ID #${unitId} not found.`);
      return wrapResult(null);
    }
  },
  
  /**
   * Get unit across all snapshots
   */
  getUnitAcrossSnapshots(unitId) {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame) {
      console.error('❌ No game loaded');
      return null;
    }
    
    const results = {};
    
    // Check in current game data
    if (rawGame.dynamic_objects?.units) {
      const unit = rawGame.dynamic_objects.units.find(u => u.id === unitId);
      if (unit) results.current_game = unit;
    }
    
    // Check in snapshots
    if (rawGame.snapshots) {
      // Base
      if (rawGame.snapshots.base?.dynamic_objects?.units) {
        const unit = rawGame.snapshots.base.dynamic_objects.units.find(u => u.id === unitId);
        if (unit) results.base = unit;
      }
      
      // Numbered
      Object.keys(rawGame.snapshots).forEach(key => {
        if (!isNaN(parseInt(key))) {
          const snapshot = rawGame.snapshots[key];
          if (snapshot?.dynamic_objects?.units) {
            const unit = snapshot.dynamic_objects.units.find(u => u.id === unitId);
            if (unit) results[`snapshot_${key}`] = unit;
          }
        }
      });
      
      // Current
      if (rawGame.snapshots.current?.dynamic_objects?.units) {
        const unit = rawGame.snapshots.current.dynamic_objects.units.find(u => u.id === unitId);
        if (unit) results.current = unit;
      }
    }
    
    if (Object.keys(results).length === 0) {
      console.error(`❌ Unit #${unitId} not found anywhere`);
      return null;
    }
    
    console.groupCollapsed(`🎮 Unit #${unitId} across snapshots`);
    Object.entries(results).forEach(([key, unit]) => {
      console.log(`📍 ${key}:`, unit);
    });
    console.groupEnd();

    return wrapResult(results);
  },
  
  /**
   * List all units in current state
   */
  listUnits() {
    const units = useGameStore.getState().getCurrentUnits();
    if (!units || units.length === 0) {
      console.error('❌ No units found');
      return wrapResult([]);
    }

    console.table(units.map(u => ({
      id: u.id,
      name: u.name || '❌ MISSING',
      hp: `${u.hp || '?'}/${u.max_hp || '?'}`,
      ap: `${u.ap || '?'}/${u.max_ap || '?'}`,
      pos: `(${u.x || '?'}, ${u.y || '?'})`,
      team: u.teamClass || '❌',
      alive: u.is_alive ? '✅' : '💀'
    })));

    return wrapResult(units);
  },
  
  // === GAME STATE FUNCTIONS ===
  
  /**
   * Get current game state (computed)
   */
  getGameState() {
    const game = useGameStore.getState().game;
    console.log("Current Game State (Computed):", game);
    return wrapResult(game);
  },
  
  /**
   * Get raw game data
   */
  getRawGameData() {
    const rawGame = useGameStore.getState()._rawGame;
    console.log('📦 Raw game data (_rawGame):', rawGame);
    return wrapResult(rawGame);
  },
  
  /**
   * Get all store states
   */
  getStores() {
    const stores = {
      game: useGameStore.getState(),
      cinematic: useCinematicStore.getState(),
      map: useMapStore.getState(),
      selection: useSelectionStore.getState(),
      auth: useAuthStore.getState(),
      uiEffects: useUiEffectsStore.getState()
    };
    return wrapResult(stores);
  },
  
  // === RESOLUTION QUEUE ===
  
  /**
   * Get resolution queue item at index
   */
  getResolutionQueue(index) {
    const queue = useGameStore.getState().game?.resolution_queue;
    if (!queue) {
      console.error('❌ No resolution queue available');
      return wrapResult(null);
    }

    if (index !== undefined) {
      console.log(`Resolution Queue [${index}]:`, queue[index]);
      return wrapResult(queue[index]);
    }

    console.log("Full Resolution Queue:", queue);
    return wrapResult(queue);
  },
  
  /**
   * Dump resolution queue with formatting
   */
  dumpResolutionQueue() {
    const game = useGameStore.getState().game;
    if (!game?.resolution_queue) {
      console.error('❌ No resolution queue');
      return null;
    }
    
    console.groupCollapsed(`📋 Resolution Queue (${game.resolution_queue.length} items)`);
    game.resolution_queue.forEach((item, index) => {
      const icon = item.event_type === 'action' ? '⚔️' :
                  item.event_type === 'effect_processing' ? '⚡' : '✨';
      console.log(`${index}: ${icon} ${item.event_type}`, {
        action: item.action_type,
        unit: item.unit_id,
        isReaction: item.is_reaction,
        animations: item.animations?.length || 0
      });
    });
    console.groupEnd();

    return wrapResult(game.resolution_queue);
  },
  
  /**
   * Get animations from resolution queue
   */
  getAnimation(index) {
    const game = useGameStore.getState().game;
    if (!game || !game.resolution_queue) {
      console.warn('❌ No resolution queue available');
      return null;
    }

    // If no index provided, return all animations from all items
    if (index === undefined) {
      const allAnimations = [];
      game.resolution_queue.forEach((item, idx) => {
        if (item.animations && item.animations.length > 0) {
          allAnimations.push({
            index: idx,
            animations: item.animations
          });
        }
      });
      console.log('All Animations:', allAnimations);
      return wrapResult(allAnimations);
    }

    // Get specific item from resolution queue
    const item = game.resolution_queue[index];
    if (!item) {
      console.warn(`❌ No item at index ${index} in resolution queue`);
      return wrapResult(null);
    }

    const animations = item.animations || [];
    console.log(`Animations at index [${index}]:`, animations);
    return wrapResult(animations);
  },

  /**
   * Get animation timing table for a specific RQ index
   * Shows delay, duration, and end time for all animations
   */
  getAnimationTiming(index) {
    const game = useGameStore.getState().game;
    if (!game || !game.resolution_queue) {
      console.error('❌ No resolution queue available');
      return null;
    }

    // Get playback speed for MS calculation
    const playbackSpeed = useCinematicStore.getState().playbackSpeed || 1;
    const tickDurationMS = CONFIG.TICK_DURATION_MS / playbackSpeed;

    // Helper to calculate MS from ticks
    const ticksToMS = (ticks) => Math.round((ticks || 0) * tickDurationMS);

    // If no index provided, show all RQ items with animations
    if (index === undefined) {
      console.log('%c⏱️ Animation Timing - All RQ Items', 'color: #FFD700; font-size: 14px; font-weight: bold');
      console.log(`(Playback speed: ${playbackSpeed}x, Tick = ${tickDurationMS}ms)`);

      const allTimings = [];
      game.resolution_queue.forEach((item, idx) => {
        if (item.animations && item.animations.length > 0) {
          console.groupCollapsed(`RQ[${idx}]: ${item.event_type} - ${item.animations.length} animations`);
          this._printAnimationTimingTable(item.animations, ticksToMS);
          console.groupEnd();
          allTimings.push({ index: idx, event_type: item.event_type, animations: item.animations });
        }
      });
      return wrapResult(allTimings);
    }

    // Get specific item
    const item = game.resolution_queue[index];
    if (!item) {
      console.error(`❌ No item at index ${index} in resolution queue`);
      return null;
    }

    const animations = item.animations || [];
    if (animations.length === 0) {
      console.warn(`⚠️ No animations at RQ[${index}]`);
      return null;
    }

    console.log(`%c⏱️ Animation Timing - RQ[${index}]`, 'color: #FFD700; font-size: 14px; font-weight: bold');
    console.log(`Event: ${item.event_type} | Action: ${item.action_type || 'N/A'}`);
    console.log(`Playback speed: ${playbackSpeed}x | Tick = ${tickDurationMS}ms`);

    this._printAnimationTimingTable(animations, ticksToMS);

    // Calculate total timeline
    const maxEndTime = Math.max(...animations.map(a => (a.delay || 0) + (a.duration || 0)));
    console.log(`\n📊 Total timeline: ${maxEndTime} ticks (${ticksToMS(maxEndTime)}ms)`);

    return wrapResult({ event_type: item.event_type, action_type: item.action_type, animations, totalTicks: maxEndTime });
  },

  /**
   * Internal helper to print animation timing table
   * @private
   */
  _printAnimationTimingTable(animations, ticksToMS) {
    const tableData = animations.map((anim, i) => {
      const delay = anim.delay || 0;
      const duration = anim.duration || 0;
      const endTime = delay + duration;

      return {
        '#': i,
        'Name': anim.animation_name || '?',
        'Category': anim.category || '?',
        'Delay': `${delay}t`,
        'Delay(ms)': ticksToMS(delay),
        'Duration': `${duration}t`,
        'Dur(ms)': ticksToMS(duration),
        'End': `${endTime}t`,
        'End(ms)': ticksToMS(endTime)
      };
    });

    console.table(tableData);
  },
  
  // === VALIDATION ===
  
  /**
   * Validate all game data
   */
  validateGameData() {
    const game = useGameStore.getState().game;
    if (!game) {
      console.error('❌ No game loaded');
      return;
    }
    
    console.log('🔍 Running validation...');
    validateGameSnapshots(game);
  },
  
  /**
   * Validate specific unit
   */
  validateUnit(unitId) {
    const game = useGameStore.getState().game;
    if (!game) {
      console.error('❌ No game loaded');
      return;
    }
    
    validateUnitAcrossSnapshots(game, unitId);
  },
  
  /**
   * Validate current units
   */
  validateCurrentUnits() {
    const units = useGameStore.getState().getCurrentUnits();
    validateUnits(units, 'Current Units (Manual Check)');
  },
  
  // === ACTIONS & PLANNING ===
  
  /**
   * Get team planned actions
   */
  getPlannedActions() {
    const actions = useGameStore.getState().game?.team_planned_actions || [];

    if (actions.length === 0) {
      console.log("No planned actions.");
      return wrapResult([]);
    }

    // Calculate origins for better visualization
    const game = useGameStore.getState().game;
    const origins = calculateActionOrigins(game);

    // Add calculated origins to each action
    const actionsWithOrigins = actions.map(action => ({
      ...action,
      calculatedOrigin: origins[action.unit_id] || null
    }));

    console.table(actionsWithOrigins.map(a => ({
      id: a.id,
      action: a.action?.name || 'Unknown',
      unit: a.unit?.name || `Unit #${a.unit_id}`,
      origin: a.calculatedOrigin ? `(${a.calculatedOrigin.x}, ${a.calculatedOrigin.y})` : 'Unknown',
      target: a.target_data ? `(${a.target_data.x}, ${a.target_data.y})` : 'N/A',
      status: a.status
    })));

    return wrapResult(actionsWithOrigins);
  },
  
  // === CINEMATIC ===
  
  /**
   * Get cinematic state
   */
  getCinematicState() {
    const state = useCinematicStore.getState();
    console.groupCollapsed('🎬 Cinematic State');
    console.log('Current Step:', state.currentActionCardIndex);
    console.log('Executed Step:', state.executedResolutionQueueIndex);
    console.log('Is Animating:', state.isAnimating);
    console.log('Auto Play:', state.isAutoPlay);
    console.log('Playback Speed:', state.playbackSpeed);
    console.groupEnd();
    return wrapResult(state);
  },
  
  // === METADATA ===
  
  /**
   * Get game metadata (waits for it to load if necessary)
   * Won't trigger a new fetch if already loading from app initialization
   */
  async getMetadata() {
    const state = useMetadataStore.getState();
    
    // If already loaded, return immediately
    if (state.loaded && state.metadata) {
      console.log('=== Game Metadata (Cached) ===');
      console.log('Loaded:', state.loaded);
      console.log('Loading:', state.loading);
      console.log('Error:', state.error);
      console.log('\n=== Stat Constraints ===');
      console.table(state.metadata.stat_constraints);
      console.log('\n=== Tracked Stats ===');
      console.log(state.metadata.tracked_stats);
      console.log('\n=== Full Metadata ===');
      console.log(state.metadata);
      
      return wrapResult(state.metadata);
    }

    // If currently loading, wait for it to complete
    if (state.loading) {
      console.log('Metadata is loading, waiting...');
      
      // Poll until loading is complete (check every 100ms)
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const currentState = useMetadataStore.getState();
          
          if (!currentState.loading) {
            clearInterval(checkInterval);
            
            if (currentState.loaded && currentState.metadata) {
              console.log('Metadata loaded successfully!');
              console.log('\n=== Stat Constraints ===');
              console.table(currentState.metadata.stat_constraints);
              resolve(wrapResult(currentState.metadata));
            } else if (currentState.error) {
              console.error('Metadata loading failed:', currentState.error);
              reject(new Error(currentState.error));
            } else {
              console.error('Metadata loading completed but no data available');
              reject(new Error('No metadata available'));
            }
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Metadata loading timeout'));
        }, 10000);
      });
    }
    
    // If not loaded and not loading, fetch it
    console.log('Metadata not loaded, fetching...');
    try {
      const metadata = await useMetadataStore.getState().fetchMetadata();
      console.log('Metadata fetched successfully!');
      console.log('\n=== Stat Constraints ===');
      console.table(metadata.stat_constraints);
      return wrapResult(metadata);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      throw error;
    }
  },
  
  /**
   * Force fetch metadata (bypasses cache)
   */
  async fetchMetadata() {
    console.log('Force fetching metadata...');
    try {
      const metadata = await useMetadataStore.getState().fetchMetadata();
      console.log('Metadata fetched successfully!');
      console.log('\n=== Stat Constraints ===');
      console.table(metadata.stat_constraints);
      return wrapResult(metadata);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      return wrapResult(null);
    }
  },
  
  // === THEME ===

  /**
   * Reload current theme (for development - bypasses cache)
   * @param {string} slug - Optional theme slug, defaults to current theme
   */
  async reloadTheme(slug) {
    const currentSlug = slug || useConfigStore.getState().settings.theme || 'base';
    const brand = import.meta.env.VITE_GAME_BRAND || 'alberath';
    const url = `/${brand}/theme/${currentSlug}/theme.json`;

    console.log(`🎨 Reloading theme: ${currentSlug}...`);

    try {
      // Force reload with cache-busting timestamp
      const cacheBustUrl = `${url}?t=${Date.now()}`;
      const response = await fetch(cacheBustUrl);
      const themeConfig = await response.json();

      // Add timestamp to version to bust CSS cache
      themeConfig.version = `${themeConfig.version}-${Date.now()}`;

      await Asset.setTheme(themeConfig);
      console.log(`✅ Theme "${currentSlug}" reloaded!`);
      return themeConfig;
    } catch (error) {
      console.error(`❌ Failed to reload theme:`, error);
      return null;
    }
  },

  /**
   * Switch to a different theme
   * @param {string} slug - Theme slug (base, dark, etc.)
   */
  async switchTheme(slug) {
    if (!slug) {
      console.error('❌ Please provide a theme slug (e.g., "base", "dark")');
      return;
    }

    const brand = import.meta.env.VITE_GAME_BRAND || 'alberath';
    const url = `/${brand}/theme/${slug}/theme.json`;

    console.log(`🎨 Switching to theme: ${slug}...`);

    try {
      await Asset.loadTheme(url, true);
      useConfigStore.getState().updateSetting('theme', slug);
      console.log(`✅ Switched to theme "${slug}"!`);
    } catch (error) {
      console.error(`❌ Failed to switch theme:`, error);
    }
  },

  // === UTILITIES ===

  /**
   * Clear console with style
   */
  clear() {
    console.clear();
    console.log('%c🎮 Xhelos Debug Tools Ready', 'color: #549BFF; font-size: 20px; font-weight: bold');
    // console.log('%cType xhelos.help() for commands', 'color: #888; font-style: italic');
  },
  
  /**
   * Show help
   */
  help() {
    console.log('%c🎮 Xhelos Debug Tools', 'color: #549BFF; font-size: 18px; font-weight: bold');
    console.log('%c══════════════════════', 'color: #549BFF');
    
    const commands = [
      ['SNAPSHOTS', ''],
      ['xhelos.getSnapshot(key)', 'Get MERGED snapshot (base/current/1/2...)'],
      ['xhelos.getRawSnapshot(key)', 'Get RAW snapshot/delta (not merged)'],
      ['xhelos.getDeltaSnapshot(n)', 'Get only delta changes at step n'],
      ['xhelos.listSnapshots()', 'List all available snapshots'],
      ['', ''],
      ['UNITS', ''],
      ['xhelos.getUnit(id)', 'Get unit from current state'],
      ['xhelos.getUnitAcrossSnapshots(id)', 'Get unit across all snapshots'],
      ['xhelos.listUnits()', 'List all units in table'],
      ['xhelos.validateUnit(id)', 'Validate specific unit'],
      ['', ''],
      ['GAME STATE', ''],
      ['xhelos.getGameState()', 'Get current computed game state'],
      ['xhelos.getRawGameData()', 'Get raw game data (_rawGame)'],
      ['xhelos.getStores()', 'Get all store states'],
      ['xhelos.validateGameData()', 'Validate all game data'],
      ['', ''],
      ['RESOLUTION', ''],
      ['xhelos.getResolutionQueue()', 'Show full resolution queue'],
      ['xhelos.dumpResolutionQueue()', 'Pretty print resolution queue'],
      ['xhelos.getAnimation(index)', 'Get visual effects at index'],
      ['xhelos.getAnimationTiming(index)', 'Show timing table (delay/duration/end)'],
      ['', ''],
      ['ACTIONS', ''],
      ['xhelos.getPlannedActions()', 'Show planned actions with origins'],
      ['xhelos.getCinematicState()', 'Show cinematic/animation state'],
      ['', ''],
      ['METADATA', ''],
      ['xhelos.getMetadata()', 'Get game metadata (waits if loading)'],
      ['xhelos.fetchMetadata()', 'Force fetch metadata (bypasses cache)'],
      ['', ''],
      ['UTILS', ''],
      ['xhelos.clear()', 'Clear console'],
      ['xhelos.help()', 'Show this help']
    ];
    
    commands.forEach(([cmd, desc]) => {
      if (cmd === '' && desc === '') {
        console.log(''); // Empty line for spacing
      } else if (desc === '') {
        console.log(`%c${cmd}`, 'color: #FFD700; font-weight: bold'); // Section headers
      } else {
        console.log(`  ${cmd}${desc ? ' - ' + desc : ''}`);
      }
    });
    
    console.log('%c══════════════════════', 'color: #549BFF');
    console.log('💡 Tips:');
    console.log('  • Chain .str() to any command for copyable JSON');
    console.log('    Example: x.anim(1).str() or xhelos.getUnit(5).str()');
    console.log('  • getSnapshot() = merged/calculated state');
    console.log('  • getRawSnapshot() = raw delta or base/current');
    console.log('  • getDeltaSnapshot(1) = only what changed at step 1');
  }
};

/**
 * Initialize debug helpers
 */
export function initDebugHelpers() {
  // Only initialize in debug mode
  if (!CONFIG.DEBUG_MODE) return;

  // Check if already initialized
  if (window.xhelos && !window.xhelos._partial) return;

  // Attach to window
  window.xhelos = xhelosDebugger;

  // Short aliases for quick console access
  window.x = {
    anim: (i) => xhelosDebugger.getAnimation(i),
    animtime: (i) => xhelosDebugger.getAnimationTiming(i),
    unit: (id) => xhelosDebugger.getUnit(id),
    units: () => xhelosDebugger.listUnits(),
    rq: (i) => i !== undefined ? xhelosDebugger.getResolutionQueue(i) : xhelosDebugger.dumpResolutionQueue(),
    snap: (k) => xhelosDebugger.getSnapshot(k),
    raw: (k) => xhelosDebugger.getRawSnapshot(k),
    game: () => xhelosDebugger.getGameState(),
    plan: () => xhelosDebugger.getPlannedActions(),
    cine: () => xhelosDebugger.getCinematicState(),
    meta: () => xhelosDebugger.getMetadata(),
    theme: (slug) => xhelosDebugger.reloadTheme(slug),
    themeSwitch: (slug) => xhelosDebugger.switchTheme(slug),
    preload: (label = 'Test', images = ['unit.pyromancer'], force = true) => {
      Asset.preload({ label, images, force });
      console.log('Preload started:', { label, images, force });
    },
    preloadState: () => Asset.getState(),
    help: () => {
      console.log('%c⚡ Quick Shortcuts (x.*)', 'color: #FFD700; font-size: 14px; font-weight: bold');
      console.log('%c  All commands support .str() for copyable JSON!', 'color: #88FF88; font-style: italic');
      console.log('  Example: x.anim(1).str()');
      console.log('');
      console.log('  x.anim(i)     → getAnimation(i)');
      console.log('  x.animtime(i) → getAnimationTiming(i)');
      console.log('  x.unit(id)    → getUnit(id)');
      console.log('  x.units()     → listUnits()');
      console.log('  x.rq(i?)      → getResolutionQueue(i) / dumpResolutionQueue()');
      console.log('  x.snap(k)     → getSnapshot(k)');
      console.log('  x.raw(k)      → getRawSnapshot(k)');
      console.log('  x.game()      → getGameState()');
      console.log('  x.plan()      → getPlannedActions()');
      console.log('  x.cine()      → getCinematicState()');
      console.log('  x.meta()      → getMetadata()');
      console.log('  x.theme()     → reloadTheme() - reload current theme CSS');
      console.log('  x.themeSwitch(slug) → switchTheme() - switch to different theme');
      console.log('  x.preload(label?, images?) → test AssetPreloadBar');
      console.log('  x.preloadState() → get current preload state');
      console.log('%cFull commands: xhelos.help()', 'color: #888; font-style: italic');
    }
  };

  //console.log('%c🎮 Xhelos Debug Tools Initialized', 'color: #549BFF; font-size: 16px; font-weight: bold');
  // console.log('%cType xhelos.help() for commands', 'color: #888; font-style: italic');
}

// Auto-initialize if imported directly
if (CONFIG.DEBUG_MODE) {
  initDebugHelpers();
}